import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_tabletop-showcase/artifacts/4dj47y7o_file_000000006c38720abfd68ceed9ce979b~3.png";

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!password.trim()) {
      toast.error("Please enter a password");
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API}/admin/login`, { password });
      
      if (response.data.success) {
        // Store password and login time in localStorage (1 hour session)
        localStorage.setItem("adminPassword", password);
        localStorage.setItem("adminLoginTime", Date.now().toString());
        toast.success("Login successful!");
        navigate("/admin/dashboard");
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error("Invalid password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        {/* Back Link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 font-sans font-medium mb-8 group"
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          Back to Site
        </Link>

        {/* Login Card */}
        <div className="bg-white rounded-3xl p-8 md:p-10 shadow-xl border border-stone-200 animate-fade-in-up">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-8 justify-center">
            <img 
              src={LOGO_URL} 
              alt="Logo" 
              className="w-14 h-14 rounded-xl"
            />
            <div>
              <h1 className="font-serif font-bold text-xl text-slate-900">
                Admin Panel
              </h1>
              <p className="text-xs text-slate-500 font-sans">Independent Boardgames</p>
            </div>
          </div>

          {/* Lock Icon */}
          <div className="w-16 h-16 bg-[#7C3AED]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Lock className="w-8 h-8 text-[#7C3AED]" />
          </div>

          <h2 className="font-serif font-bold text-2xl text-slate-900 text-center mb-2">
            Welcome Back
          </h2>
          <p className="font-sans text-slate-600 text-center mb-8">
            Enter your admin password to continue
          </p>

          <form onSubmit={handleLogin} className="space-y-6">
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                placeholder="Enter admin password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pr-12 py-4 h-14 rounded-xl border-2 border-stone-200 focus:border-[#7C3AED] focus:ring-4 focus:ring-[#7C3AED]/10 font-sans"
                data-testid="password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                data-testid="toggle-password"
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#7C3AED] hover:bg-[#6D28D9] text-white py-4 h-14 rounded-xl font-sans font-bold text-base hover:scale-[1.02] active:scale-[0.98] transition-transform"
              data-testid="login-btn"
            >
              {loading ? "Logging in..." : "Login to Dashboard"}
            </Button>
          </form>
        </div>

        <p className="text-center text-slate-500 text-sm font-sans mt-6">
          This area is for authorized administrators only.
        </p>
      </div>
    </div>
  );
}
